import React, { use, useEffect } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { translateStatesBicci } from "../../../utils/objects/translateStates";

const TableroEmpresa = ({ mergedOrders }) => {
  const [resumen, setResumen] = React.useState([]);

  useEffect(() => {
    if (mergedOrders) {
      const resumen = procesarResumenDeOrdenes(mergedOrders);
      setResumen(resumen);
    }
  }, [mergedOrders]);

  const procesarResumenDeOrdenes = (ordenes) => {
    if (!ordenes || ordenes.length === 0) {
      return {
        totalOrders: 0,
        entregadas: 0,
        enCocina: 0,
        totalRetries: 0,
        comunas: {},
        riders: {},
        ridersUnicos: [],
      };
    }
    const resumen = {
      totalOrders: ordenes.length,
      entregadas: 0,
      enCocina: 0,
      totalRetries: 0,
      comunas: {},
      riders: {},
      porEmpresa: {},
      ridersUnicos: new Set(),
    };

    ordenes.forEach((order) => {
      if (order.status === 8) resumen.entregadas++;
      if (order.enCocina) resumen.enCocina++;
      resumen.totalRetries += order.retryCount || 0;

      // Comunas
      const comuna = order.comuna?.trim().toLowerCase() || "sin comuna";
      resumen.comunas[comuna] = (resumen.comunas[comuna] || 0) + 1;

      // Riders
      if (order.driverName) {
        resumen.riders[order.driverName] =
          (resumen.riders[order.driverName] || 0) + 1;
        resumen.ridersUnicos.add(order.driverName);
      }

      const placeName = order.placeName || "Sin Empresa";
      const origen = order.whcode || "Default";
      const status = order.status || 0;
      const keyEmpresa = `${placeName}||${origen}`;

      if (!resumen.porEmpresa[keyEmpresa]) {
        resumen.porEmpresa[keyEmpresa] = {};
      }
      resumen.porEmpresa[keyEmpresa][status] =
        (resumen.porEmpresa[keyEmpresa][status] || 0) + 1;
    });

    // Convertir Set a Array
    resumen.ridersUnicos = Array.from(resumen.ridersUnicos);

    return resumen;
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        backgroundColor: "#f8f8f8",
      }}
    >
      <Box
        sx={{
          p: 3,
          width: "48%",
          display: "flex",
          flexDirection: "row",
          backgroundColor: "#f8f8f8",
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Resumen de General
          </Typography>

          {/* Métricas generales */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell align="center">
                    <b>Métrica</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Valor</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="left">Órdenes Totales</TableCell>
                  <TableCell align="center">{resumen?.totalOrders}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">Entregadas</TableCell>
                  <TableCell align="center">{resumen?.entregadas}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">En Cocina</TableCell>
                  <TableCell align="center">{resumen?.enCocina}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">Reintentos</TableCell>
                  <TableCell align="center">{resumen?.totalRetries}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">Riders Únicos</TableCell>
                  <TableCell align="center">
                    {resumen?.ridersUnicos?.length}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="left">Comunas Distintas</TableCell>
                  <TableCell align="center">
                    {resumen?.comunas
                      ? Object.keys(resumen.comunas).length
                      : "No disponible"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box>
          {/* Recuento por comuna */}
          <Typography variant="h6" gutterBottom>
            Órdenes por Comuna
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell align="left">
                    <b>Comuna</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Cantidad</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumen?.comunas && Object.keys(resumen.comunas).length > 0 ? (
                  Object.entries(resumen.comunas).map(
                    ([comuna, cantidad], index) => (
                      <TableRow key={index}>
                        <TableCell align="left">{comuna}</TableCell>
                        <TableCell align="center">{cantidad}</TableCell>
                      </TableRow>
                    ),
                  )
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={2}>
                      No hay comunas registradas.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box>
          {/* Recuento por conductor */}
          <Typography variant="h6" gutterBottom>
            Órdenes por Conductor
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell align="left">
                    <b>Nombre</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Órdenes</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumen?.riders && Object.keys(resumen.riders).length > 0 ? (
                  Object.entries(resumen.riders).map(
                    ([rider, cantidad], index) => (
                      <TableRow key={index}>
                        <TableCell align="center">{rider}</TableCell>
                        <TableCell align="center">{cantidad}</TableCell>
                      </TableRow>
                    ),
                  )
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={2}>
                      No hay riders registrados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box></Box>
      </Box>
      <Box
        sx={{
          p: 3,
          width: "50%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#f8f8f8",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Resumen por Empresa y Estados
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ minWidth: "200px", width: "200px" }}>
                  <b>Empresa</b>
                </TableCell>
                <TableCell>
                  <b>Origen</b>
                </TableCell>
                {Array.from({ length: 8 }, (_, i) => (
                  <TableCell align="center" key={i + 1}>
                    <b>Estado {translateStatesBicci[i] || "Desconocido"}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {resumen?.porEmpresa &&
                Object.entries(resumen.porEmpresa).map(
                  ([empresaKey, estados], index) => {
                    const [placeName, origen] = empresaKey.split("||");
                    return (
                      <TableRow key={index}>
                        <TableCell sx={{ minWidth: "200px", width: "200px" }}>
                          {placeName}
                        </TableCell>
                        <TableCell>{origen}</TableCell>
                        {Array.from({ length: 8 }, (_, i) => (
                          <TableCell align="center" key={i}>
                            {estados[i + 1] || 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  },
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default TableroEmpresa;
